import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const DelightContext = createContext(null);

export function useDelight() {
  const ctx = useContext(DelightContext);
  if (!ctx) throw new Error('useDelight must be inside DelightProvider');
  return ctx;
}

export function DelightProvider({ children }) {
  const [queue, setQueue] = useState([]);           // Array of progression payloads
  const [current, setCurrent] = useState(null);      // Currently displaying item
  const [anticipation, setAnticipation] = useState(null); // Anticipation data (persists)
  const processingRef = useRef(false);
  const idCounterRef = useRef(0);                    // monotonic id stamped on each queued item
  const presentedIdRef = useRef(null);              // id of the last item handed to the modal layer
  const queryClient = useQueryClient();

  const enqueueDelight = useCallback((progression) => {
    if (!progression) return;

    // ADR-047: invalidate map cache whenever the backend signals a refresh
    // (level transition that should update the rendered biome). This is the
    // single funnel for every progression payload, so wiring it here covers
    // every caller (stories, practice, characters, worksheets) without
    // each having to remember.
    if (progression.map_should_refresh === true) {
      queryClient.invalidateQueries({ queryKey: ['mapData'] });
    }

    // FP-DELIGHT-CONTEXT-01 (B6/B7): refresh learner progress after EVERY XP
    // event so LearnerSidebar + StoryShipOverlay don't show pre-session XP /
    // streak (useProgress has a 5-min staleTime). Fires unconditionally — even
    // for 'silent' tier, since the XP total still changed. useProgress's key is
    // ['progress', childId]; invalidating the ['progress'] prefix matches it for
    // every learner without threading childId into this top-level provider.
    queryClient.invalidateQueries({ queryKey: ['progress'] });

    // Update anticipation from the latest progression
    const anticipationData = {
      near_level_up: progression.near_level_up,
      near_graduation: progression.near_graduation,
      next_unlock: progression.next_unlock,
      streak: progression.streak,
      level: progression.level,
    };
    setAnticipation(anticipationData);

    // Only queue if display_tier is not 'silent'
    if (progression.display_tier === 'silent') return;

    // Check for level-up — split into two queue items
    const isLevelUp = progression.level?.changed &&
      progression.level.current > (progression.level.previous || 0);

    if (isLevelUp) {
      // Item 1: The original event (modal/toast) — remove level_up from xp_breakdown
      // so it doesn't show duplicate XP in the first modal
      const primaryBreakdown = (progression.xp_breakdown || [])
        .filter(item => item.type !== 'level_up');
      const primaryXp = primaryBreakdown.reduce((sum, item) => sum + item.xp, 0);

      const primaryEvent = {
        ...progression,
        xp_awarded: primaryXp,
        xp_breakdown: primaryBreakdown,
      };

      // Item 2: Synthetic level-up celebration
      const levelUpEvent = {
        event_type: 'level_up',
        display_tier: 'milestone',
        display_variant: 'celebration',
        xp_awarded: 100,
        level: progression.level,
        unlocks_granted: progression.unlocks_granted || [],
        streak: progression.streak,
      };

      // Stamp ids OUTSIDE the updater so a StrictMode double-invoke of the
      // updater can't increment the counter twice / produce mismatched ids.
      const primaryId = ++idCounterRef.current;
      const levelUpId = ++idCounterRef.current;
      setQueue(prev => [
        ...prev,
        { ...primaryEvent, _delightId: primaryId },
        { ...levelUpEvent, _delightId: levelUpId },
      ]);
    } else {
      const id = ++idCounterRef.current;
      setQueue(prev => [...prev, { ...progression, _delightId: id }]);
    }
  }, [queryClient]);

  const dismiss = useCallback(() => {
    setCurrent(null);
    processingRef.current = false;
  }, []);

  // Process queue when items are added and nothing is currently displaying.
  // processingRef is set synchronously before any state update, closing the
  // re-entrancy gap if this effect runs twice in one commit (StrictMode /
  // dependency re-trigger). presentedIdRef adds a second layer: an item whose
  // id was just presented is never presented again — it's dropped from the
  // queue. Together these guarantee one modal per distinct XP event (B2).
  useEffect(() => {
    if (processingRef.current || current !== null || queue.length === 0) return;

    const [next, ...rest] = queue;

    // Already presented this exact item — drop it instead of re-showing.
    if (next?._delightId != null && next._delightId === presentedIdRef.current) {
      setQueue(rest);
      return;
    }

    processingRef.current = true;
    presentedIdRef.current = next?._delightId ?? null;
    setCurrent(next);
    setQueue(rest);
  }, [queue, current]);

  // When current is dismissed, wait briefly then process next
  useEffect(() => {
    if (current === null && processingRef.current && queue.length > 0) {
      const timer = setTimeout(() => {
        processingRef.current = false;
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [current, queue]);

  return (
    <DelightContext.Provider value={{ enqueueDelight, current, dismiss, anticipation }}>
      {children}
    </DelightContext.Provider>
  );
}
